import isFunction from 'lodash/isFunction'
import axios from 'axios'

import { useTokenManager } from '@/utils/authorization/token-manager'
import { maEnv } from '@/lib/env'
import { isValueEmpty } from '@/components/tables/ag-grid/common/cell-value'
import { defaultParamsSerializer } from './utils/params-serializer'
import { mockAdapter } from './utils/mock-adapter'
import { ApiError, createMissingTokenError, processError } from './utils/api-error'

const defaultConfig = {
  baseURL: maEnv.VITE_APP_GATEWAY_DOMAIN,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: defaultParamsSerializer,
}

const apiClient = axios.create(defaultConfig)

// Mock interceptor
apiClient.interceptors.request.use((config) => {
  if (config.mock) {
    config.adapter = mockAdapter
    config.disableAuthCheck = true
    config.baseURL = '' // Disable baseURL for mock requests
  }
  return config
})

apiClient.interceptors.request.use(
  (config) => {
    if (!config?.disableAuthCheck) {
      const { hasToken, getAccessToken } = useTokenManager()

      if (!hasToken()) {
        throw createMissingTokenError()
      }

      const token = getAccessToken()
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => Promise.reject(processError(error)),
)

apiClient.interceptors.response.use(
  (response) => {
    const res = response.data

    if (!res.success) {
      const message = res.errorMessage || 'API Error'
      const errors = Array.isArray(res.errors)
        ? res.errors.map(err => ({ message: err }))
        : []

      return Promise.reject(new ApiError(message, {
        statusCode: response.status,
        errorCode: res?.errorCode,
        errors,
      }))
    }

    if (isValueEmpty(res?.data)) {
      res.data = null
    }

    if (isFunction(response?.config?.transformData)) {
      return response.config.transformData(res.data)
    }

    return res.data
  },
  async (err) => {
    const { config, response } = err
    if (
      typeof config === 'object'
      && !config.disableAuthCheck
      && response?.status === 401
      && !config?._retry
    ) {
      config._retry = true

      const { isTokenRefresing, subscribers, onRefreshed, onRefreshFailed } = useTokenManager()

      if (!isTokenRefresing.value) {
        isTokenRefresing.value = true

        const { refreshToken, logoutAndCleanup } = useTokenManager()

        try {
          const newToken = await refreshToken()

          onRefreshed(newToken)

          config.headers.Authorization = `Bearer ${newToken}`
          return apiClient.request(config)
        }
        catch (e) {
          const error = e instanceof ApiError ? e : processError(e)
          onRefreshFailed(error)
          await logoutAndCleanup(error.errors)
          return Promise.reject(error)
        }
        finally {
          isTokenRefresing.value = false
        }
      }
      else {
        return new Promise((resolve, reject) => {
          subscribers.value.push((token, error) => {
            if (token) {
              config.headers.Authorization = `Bearer ${token}`
              resolve(apiClient.request(config))
            }
            else {
              reject(error)
            }
          })
        })
      }
    }

    return Promise.reject(processError(err))
  },
)

export { apiClient }
